import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * ClientReaffirmUpload.jsx
 *
 * Endpoint:
 * - POST ${API_URL}/client/reaffirmations/:token/upload/  (multipart: files[])
 */
const API_URL = process.env.REACT_APP_API_URL;
export default function ClientReaffirmUpload() {
  const { token } = useParams();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit = useMemo(() => files.length > 0 && !busy, [files, busy]);

  async function submit() {
    setBusy(true);
    setResult(null);

    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));

      const res = await fetch(`${API_URL}/client/reaffirmations/${token}/upload/`, {
        method: "POST",
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Upload failed.");

      setResult({ type: "success", msg: "Uploaded. Your attorney will review it shortly." });
      setFiles([]);
    } catch (e) {
      setResult({ type: "error", msg: e.message || "Upload failed." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-gray-900">Upload Signed Reaffirmation</h1>
        <p className="mt-2 text-sm text-gray-700">
          Please follow these steps carefully:
        </p>
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <div className="text-xl" aria-hidden="true">⚠️</div>
            <div>
              <div className="text-base font-extrabold text-amber-900">
                IMPORTANT: Upload ALL SIGNATURE PAGES at once (if there are more than one).
              </div>
              <p className="mt-1 text-sm font-semibold text-amber-900">
                Before you press <span className="underline">Upload</span>, you must select <span className="underline">all signature pages</span> (all PDFs)
                in the file picker <span className="underline">in one selection</span>.
              </p>
              <p className="mt-2 text-sm text-amber-900">
                If you upload only part of the pages and then upload more later, your submission may be incomplete.
                <strong> Please attach everything first, then upload one time.</strong>
              </p>
            </div>
          </div>
        </div>

        <ol className="mt-4 space-y-2 text-sm text-gray-800 list-decimal list-inside">
          <li>Print the signature pages.</li>
          <li>Sign in ink (no typed signatures).</li>
          <li>Scan the signed pages or use a scan app like CamScan, Adobe Scan or similar.</li>
          <li>Upload below as a PDF.</li>
        </ol>

        <div className="mt-6 rounded-xl bg-gray-50 ring-1 ring-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-900">Choose files</div>
          <div className="text-xs text-gray-600 mt-1">
            Accepted: PDF only and must be a scanned document.
          </div>
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>Do not upload in multiple batches.</strong> Select <strong>all</strong> pages/files/pictures now, then press <strong>Upload</strong> one time.
          </div>

          <input
            type="file"
            multiple
            accept="application/pdf"
            className="mt-3 block w-full text-sm text-gray-900 file:mr-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:text-white hover:file:bg-indigo-500"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
            disabled={busy}
          />

          {files.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-gray-600 mb-2">Selected:</div>
              <ul className="space-y-1 text-xs text-gray-800">
                {files.map((f, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2">
                    <span className="truncate">{f.name}</span>
                    <span className="text-gray-500">{Math.round(f.size / 1024)} KB</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {result && (
          <div
            className={`mt-5 rounded-xl p-4 ring-1 ${
              result.type === "success"
                ? "bg-green-50 ring-green-200 text-green-800"
                : "bg-red-50 ring-red-200 text-red-800"
            }`}
          >
            {result.msg}
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={() => setFiles([])}
            disabled={busy || files.length === 0}
            className="px-4 py-2 rounded-lg bg-white text-gray-900 hover:bg-gray-50 ring-1 ring-gray-300 disabled:opacity-50"
          >
            Clear
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {busy ? "Uploading…" : "Upload (one time)"}
          </button>
        </div>

        <p className="mt-6 text-xs text-gray-600">
          If your upload is rejected, you’ll receive an email with the reason and a link to upload again.
        </p>
      </div>
    </div>
  );
}